import React, { useEffect, useMemo, useState } from 'react';
import {
  loadWorkspaceEvaluationPlanAppliedSnapshots,
  resolveWorkspaceEvaluationPlanApplied,
} from '../../utils/workspaceEvaluationPlanAppliedCache';
import type { WorkspaceEvaluationPlanAppliedSnapshot } from '../../services/workspaceEvaluationPlanAppliedService';
import {
  comparePlansByLinkedStandards,
  formatPlanListSummary,
  formatStandardSummary,
  compareStandardsByCode,
  type EvaluationStandardDraft,
} from '../../types/evaluationPlan';

function linkedStandardsLabel(snap: WorkspaceEvaluationPlanAppliedSnapshot): string {
  const standards = [...(snap.standards ?? [])].sort(compareStandardsByCode);
  if (!standards.length) return '';
  return standards
    .slice(0, 2)
    .map((s) => formatStandardSummary(s))
    .join(' · ');
}

const EvaluationPlanAppliedPickerModal: React.FC<{
  open: boolean;
  workspaceId: string;
  gradeId: string;
  onClose: () => void;
  onPick: (snap: WorkspaceEvaluationPlanAppliedSnapshot) => void;
}> = ({ open, workspaceId, gradeId, onClose, onPick }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<WorkspaceEvaluationPlanAppliedSnapshot[]>([]);

  useEffect(() => {
    if (!open) return;
    const ws = workspaceId.trim();
    const gid = gradeId.trim();
    if (!ws || !gid) {
      setItems([]);
      setError('클래스를 선택해 주세요.');
      setLoading(false);
      return;
    }

    const cached = loadWorkspaceEvaluationPlanAppliedSnapshots(ws, gid);
    if (cached.found) {
      setItems(cached.snapshots);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void resolveWorkspaceEvaluationPlanApplied(ws, gid)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '적용 평가계획을 불러오지 못했습니다.');
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, gradeId]);

  const sorted = useMemo(() => {
    const standards = items.flatMap((row) => row.standards ?? []) as EvaluationStandardDraft[];
    return [...items].sort((a, b) => comparePlansByLinkedStandards(a.plan, b.plan, standards));
  }, [items]);

  if (!open) return null;

  return (
    <div
      className="cp-modal-backdrop cp-evaluation-plan-applied-picker-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="cp-modal cp-evaluation-plan-applied-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-evaluation-plan-applied-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-evaluation-plan-applied-picker-title">클래스 평가계획 가져오기</h3>
        <p className="cp-evaluation-plan-level-settings-hint" role="status">
          기본 평가계획 등록에서 클래스에 적용한 평가계획을 선택합니다.
        </p>
        <div className="cp-modal-body cp-evaluation-plan-applied-picker-body">
          {loading ? <p role="status">불러오는 중…</p> : null}
          {error ? <p className="cp-admin-learning-error">{error}</p> : null}
          {!loading && !error && sorted.length === 0 ? (
            <p role="status">적용된 평가계획이 없습니다. 기본 평가계획 등록에서 먼저 적용해 주세요.</p>
          ) : null}
          {!loading && sorted.length > 0 ? (
            <ul className="cp-evaluation-plan-applied-picker-list">
              {sorted.map((snap) => {
                const summary = formatPlanListSummary(snap.plan);
                const stdLabel = linkedStandardsLabel(snap);
                const category = (snap.source_category_name || snap.plan.category_name || '').trim();
                return (
                  <li key={snap.id}>
                    <button
                      type="button"
                      className="cp-evaluation-plan-applied-picker-item"
                      onClick={() => onPick(snap)}
                    >
                      {category ? (
                        <span className="cp-evaluation-plan-applied-picker-item-cat">{category}</span>
                      ) : null}
                      <span className="cp-evaluation-plan-applied-picker-item-summary">{summary}</span>
                      {stdLabel ? (
                        <span className="cp-evaluation-plan-applied-picker-item-std">{stdLabel}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanAppliedPickerModal;
